import { useMemo } from "react";
import { useSearchParams, type SetURLSearchParams } from "react-router";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { type DocumentSummary } from "~/api/types";
import { useDocuments } from "~/api/v2/useDocuments";
import {
  filterDocuments,
  multiSelectFilterKeys,
} from "~/helpers/documentFilters";
import { useDocumentFilters } from "~/hooks/useDocumentFilters";
import { includesSearch } from "~/helpers/string";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";
import { useUserContext } from "~/hooks/useUserContext";
import { PlatformRole } from "~/config/types";
import { QueryParams } from "~/api/v2/useDocumentReleases";
import { getDocumentsListColumnSpecs } from "~/helpers/documentsListColumns";
import { librarySortParam } from "~/helpers/libraryViewPersistence";
import { useLibraryViewPersistence } from "~/hooks/useLibraryViewPersistence";

const defaultSorting: SortingState = [{ id: "title", desc: false }];

const parseSortingFromParams = (
  searchParams: URLSearchParams,
  availableColumnIds: readonly string[],
): SortingState => {
  const sortValue = searchParams.get(librarySortParam);
  if (!sortValue) return defaultSorting;
  const match = /^([^:]+):(asc|desc)$/.exec(sortValue);
  if (!match || !availableColumnIds.includes(match[1]!)) return defaultSorting;
  return [{ id: match[1]!, desc: match[2] === "desc" }];
};

const readerLibraryMultiSelectFilterKeys = multiSelectFilterKeys.filter(
  (key) => key !== "archiveState",
);

const updateSearchParams = (
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  apply: (next: URLSearchParams) => void,
) => {
  const next = new URLSearchParams(searchParams);
  apply(next);
  setSearchParams(next);
};

export const useDocumentsListModel = () => {
  const [urlSearchParams, setSearchParams] = useSearchParams();
  const isRefNumEnabled =
    useFeatureFlagEnabled(featureFlags.referenceNumber) ?? false;

  const { data, isLoading, mutate } = useDocuments({ includeArchived: true });
  const { user } = useUserContext();
  const {
    searchParams,
    setLibraryViewParams,
    isPersistenceEnabled,
    setIsPersistenceEnabled,
  } = useLibraryViewPersistence({
    searchParams: urlSearchParams,
    setSearchParams,
    userId: user.id,
  });
  const titleSearch = searchParams.get(QueryParams.Title);

  const documents = data ?? [];
  const filters = useDocumentFilters({
    documents,
    searchParams,
    setSearchParams: setLibraryViewParams,
    multiSelectFields:
      user.role === PlatformRole.reader
        ? readerLibraryMultiSelectFilterKeys
        : multiSelectFilterKeys,
  });

  const columnSpecs = useMemo(
    () => getDocumentsListColumnSpecs({ referenceNumber: isRefNumEnabled }),
    [isRefNumEnabled],
  );
  const sorting = parseSortingFromParams(
    searchParams,
    columnSpecs.map((spec) => spec.id),
  );

  const filteredDocuments: DocumentSummary[] = filterDocuments(
    documents,
    filters.activeFilters,
  ).filter(
    (doc) =>
      !titleSearch ||
      includesSearch(doc.latestRelease.artifact.title, titleSearch) ||
      (isRefNumEnabled &&
        includesSearch(doc.referenceNumber ?? "", titleSearch)),
  );

  const handleSortingChange = (updater: Updater<SortingState>) => {
    const nextSorting =
      typeof updater === "function" ? updater(sorting) : updater;

    updateSearchParams(searchParams, setLibraryViewParams, (next) => {
      const firstSort = nextSorting[0];
      if (firstSort) {
        next.set(
          librarySortParam,
          `${firstSort.id}:${firstSort.desc ? "desc" : "asc"}`,
        );
      } else {
        next.delete(librarySortParam);
      }
    });
  };

  const handleTitleSearchChange = (value: string) => {
    updateSearchParams(searchParams, setSearchParams, (next) => {
      if (value) {
        next.set(QueryParams.Title, value);
      } else {
        next.delete(QueryParams.Title);
      }
    });
  };

  return {
    columnSpecs,
    documents,
    filteredDocuments,
    filters,
    hasData: data !== undefined,
    isLoading,
    isViewPersistenceEnabled: isPersistenceEnabled,
    setIsViewPersistenceEnabled: setIsPersistenceEnabled,
    mutate,
    sorting,
    titleSearch: titleSearch ?? "",
    handleSortingChange,
    handleTitleSearchChange,
  };
};

export type DocumentsListModel = ReturnType<typeof useDocumentsListModel>;
